import { AppShell, Burger, Button, Group, Modal, NavLink, Stack, Text, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconCalendarStats,
  IconChartLine,
  IconHistory,
  IconLogout,
  IconSalad,
  IconSettings,
  IconTarget,
} from "@tabler/icons-react";
import { useState } from "react";
import { NavLink as RouterLink, Outlet, useLocation } from "react-router";

import { useLogout, useMe } from "../lib/auth";
import { useSyncState } from "../sync/actions";
import { SyncRunner } from "../sync/SyncRunner";
import { PullToRefresh } from "./PullToRefresh";
import { SyncStatus } from "./SyncStatus";

const LINKS = [
  { to: "/", label: "Today", icon: IconCalendarStats },
  { to: "/history", label: "History", icon: IconChartLine },
  { to: "/foods", label: "Foods", icon: IconSalad },
  { to: "/goals", label: "Goals", icon: IconTarget },
  { to: "/activity", label: "Activity", icon: IconHistory },
  { to: "/settings", label: "Settings", icon: IconSettings },
];

export function Layout() {
  const [opened, { toggle, close }] = useDisclosure();
  const { pathname } = useLocation();
  // Close the phone menu on any navigation (links, back button, table rows).
  const [menuPath, setMenuPath] = useState(pathname);
  if (menuPath !== pathname) {
    setMenuPath(pathname);
    close();
  }
  const me = useMe();
  const logout = useLogout();
  const { pending, failed } = useSyncState();
  const unsynced = pending.length + failed.length;
  const [confirmSignOut, setConfirmSignOut] = useState(false);

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 220, breakpoint: "sm", collapsed: { mobile: !opened } }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group gap="xs">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" aria-label="Menu" />
            <Title order={4}>🥗 Calorie Tracking</Title>
          </Group>
          <Group gap="sm">
            <SyncStatus />
            <Text size="sm" c="dimmed" visibleFrom="xs">
              {me.data?.username}
            </Text>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="xs">
        {LINKS.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            component={RouterLink}
            to={to}
            label={label}
            leftSection={<Icon size={18} stroke={1.6} />}
            active={pathname === to}
            onClick={close}
          />
        ))}
        <NavLink
          mt="auto"
          label="Sign out"
          leftSection={<IconLogout size={18} stroke={1.6} />}
          onClick={() => (unsynced > 0 ? setConfirmSignOut(true) : void logout())}
        />
      </AppShell.Navbar>

      <AppShell.Main maw={960}>
        <SyncRunner />
        <PullToRefresh>
          <Outlet />
        </PullToRefresh>
      </AppShell.Main>

      <Modal opened={confirmSignOut} onClose={() => setConfirmSignOut(false)} title="Changes not synced yet">
        <Stack>
          <Text size="sm">
            {unsynced} change{unsynced === 1 ? " hasn't" : "s haven't"} reached the server yet. They
            stay on this device and are sent the next time you sign in here as {me.data?.username}.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmSignOut(false)}>
              Stay signed in
            </Button>
            <Button color="red" onClick={() => void logout()}>
              Sign out anyway
            </Button>
          </Group>
        </Stack>
      </Modal>
    </AppShell>
  );
}
